/**
 * Response to monthly points, one adapter per response shape (#2175 B1).
 * Port of mobile-management/src/features/metrics/adapters.ts (#1989 G1); the
 * logic is unchanged and adapters.test.ts carries the phone's fixtures. Twelve
 * cockpit endpoints share the `CockpitResponse` envelope; the others carry
 * their own period shape. Every adapter is pure and tolerant: a missing array
 * or field reads as an empty month, never a crash.
 */

import type { MetricPoint, MetricSummaryTotals } from "./types";

type Json = Record<string, unknown>;

function asArray(value: unknown): Json[] {
  return Array.isArray(value) ? (value as Json[]) : [];
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function numOr0(value: unknown): number {
  return num(value) ?? 0;
}

function periodOf(row: Json, key = 'period'): string {
  const raw = row[key];
  return typeof raw === 'string' ? raw.slice(0, 7) : '';
}

function breakdownOf(value: unknown): Record<string, number> | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(value as Json)) {
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * The `CockpitResponse` envelope: `periods[] { period, value, previous_value,
 * has_previous, breakdown? }`. `has_previous: false` means the comparison month
 * had no data, so its `previous_value` of 0 is no value (#1989 D12); a server
 * without the flag keeps the value.
 */
export function adaptCockpitEnvelope(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.periods).map((p) => ({
    period: periodOf(p),
    value: num(p.value),
    previous: p.has_previous === false ? null : num(p.previous_value),
    breakdown: breakdownOf(p.breakdown),
  }));
}

/**
 * The envelope with selected breakdown keys only (app usage counts events, but
 * its breakdown also holds `unique_employees`, which is not a part of the sum).
 */
export function adaptCockpitEnvelopeParts(keys: readonly string[]) {
  return (response: unknown): MetricPoint[] =>
    adaptCockpitEnvelope(response).map((p) => {
      if (!p.breakdown) return p;
      const parts: Record<string, number> = {};
      for (const k of keys) if (k in p.breakdown) parts[k] = p.breakdown[k];
      return { ...p, breakdown: parts };
    });
}

/** The envelope without a breakdown (a breakdown that is not parts of the value). */
export function adaptCockpitEnvelopeNoParts(response: unknown): MetricPoint[] {
  return adaptCockpitEnvelope(response).map(({ breakdown: _b, ...p }) => p);
}

/**
 * Care ratio: per period the groups' actual staff-per-child ratio. Shown as
 * children per staff member (1 : x), the way a Kita states its Betreuungsschluessel;
 * the mean of the groups' ratios, inverted. No previous-year figure.
 */
export function adaptCareRatio(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.periods).map((p) => {
    const ratios = asArray(p.groups)
      .map((g) => num(g.actual_ratio))
      .filter((r): r is number => r !== null && r > 0);
    const mean =
      ratios.length > 0 ? ratios.reduce((a, b) => a + b, 0) / ratios.length : 0;
    return { period: periodOf(p), value: mean > 0 ? 1 / mean : null, previous: null };
  });
}

/** Care-ratio violations: days below the required ratio, summed over the groups. */
export function adaptViolations(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.periods).map((p) => ({
    period: periodOf(p),
    value: asArray(p.groups).reduce((sum, g) => sum + numOr0(g.violation_days), 0),
    previous: null,
  }));
}

/** Activities: the count-weighted mean of the activity types' average minutes. */
export function adaptActivityMinutes(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.periods).map((p) => {
    const types = asArray(p.activities);
    const count = types.reduce((sum, t) => sum + numOr0(t.count), 0);
    const minutes = types.reduce(
      (sum, t) => sum + numOr0(t.avg_minutes) * numOr0(t.count),
      0,
    );
    const breakdown: Record<string, number> = {};
    for (const t of types) {
      if (typeof t.type === 'string') breakdown[t.type] = numOr0(t.count);
    }
    return {
      period: periodOf(p),
      value: count > 0 ? minutes / count : null,
      previous: null,
      breakdown,
    };
  });
}

/** Child enrollment movement: net change, with new and ended as the parts. */
export function adaptEnrollmentMovement(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.periods).map((p) => ({
    period: periodOf(p),
    value: numOr0(p.net_delta),
    previous: null,
    breakdown: { new: numOr0(p.new_count), ended: numOr0(p.ended_count) },
  }));
}

/** Contract movement: new contracts, with new and ended as the parts. */
export function adaptContracts(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.periods).map((p) => ({
    period: periodOf(p),
    value: numOr0(p.new_count),
    previous: null,
    breakdown: { new: numOr0(p.new_count), ended: numOr0(p.ended_count) },
  }));
}

/** Cash-in: received per month (parents + offices); months without cash are 0. */
export function adaptCashIn(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.series).map((b) => {
    const parent = numOr0(b.received_parent_cents);
    const subsidy = numOr0(b.received_subsidy_cents);
    return {
      period: periodOf(b, 'bucket'),
      value: parent + subsidy,
      previous: null,
      breakdown: { parent, subsidy },
    };
  });
}

/** Liquidity forecast: expected inflow per future month. */
export function adaptLiquidity(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.periods).map((p) => ({
    period: periodOf(p),
    value: numOr0(p.expected_inflow),
    previous: null,
  }));
}

/** Payment behaviour: the amount outstanding per month, its age buckets as parts. */
export function adaptPayments(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.periods).map((p) => {
    const buckets = asArray(p.buckets);
    const breakdown: Record<string, number> = {};
    for (const b of buckets) {
      if (typeof b.label === 'string') breakdown[b.label] = numOr0(b.amount);
    }
    return {
      period: periodOf(p),
      value: buckets.reduce((sum, b) => sum + numOr0(b.amount), 0),
      previous: null,
      breakdown,
    };
  });
}

/** Payment methods: payments per month, by method. */
export function adaptPaymentMethods(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.trend).map((t) => {
    const breakdown = breakdownOf(t.methods) ?? {};
    return {
      period: periodOf(t),
      value: Object.values(breakdown).reduce((a, b) => a + b, 0),
      previous: null,
      breakdown,
    };
  });
}

/**
 * Year-end forecast per month, by the backend's own year-end rule
 * (`forecast_service.go`, `ProjectedYearEndCents`): a past or current month
 * counts what is invoiced, a future month what is contracted. Adding invoiced,
 * not-yet-invoiced and contracted for every month counted months twice.
 */
export function adaptForecast(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.series).map((p) => {
    const future = p.is_future === true;
    const invoiced = future
      ? 0
      : numOr0(p.invoiced_parent_cents) + numOr0(p.invoiced_subsidy_cents);
    const contracted = future
      ? numOr0(p.contracted_parent_cents) + numOr0(p.contracted_subsidy_cents)
      : 0;
    return {
      period: periodOf(p),
      value: invoiced + contracted,
      previous: null,
      breakdown: { invoiced, contracted },
    };
  });
}

/**
 * Year-end forecast as a running total within each calendar year (#1989 D9a):
 * a month's value is the revenue of its year from January through that month,
 * so the last month of a year is the projected year end. The parts run along.
 */
export function adaptForecastCumulative(response: unknown): MetricPoint[] {
  let year = '';
  const run = { invoiced: 0, contracted: 0 };
  return adaptForecast(response).map((p) => {
    if (p.period.slice(0, 4) !== year) {
      year = p.period.slice(0, 4);
      run.invoiced = 0;
      run.contracted = 0;
    }
    run.invoiced += p.breakdown?.invoiced ?? 0;
    run.contracted += p.breakdown?.contracted ?? 0;
    return {
      period: p.period,
      value: run.invoiced + run.contracted,
      previous: null,
      breakdown: { ...run },
    };
  });
}

/** Workflow processing time: the count-weighted median minutes of the categories. */
export function adaptProcessingTime(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.periods).map((p) => {
    const cats = asArray(p.categories);
    const count = cats.reduce((sum, c) => sum + numOr0(c.count), 0);
    const weighted = cats.reduce(
      (sum, c) => sum + numOr0(c.median_minutes) * numOr0(c.count),
      0,
    );
    const breakdown: Record<string, number> = {};
    for (const c of cats) {
      if (typeof c.category === 'string') breakdown[c.category] = numOr0(c.count);
    }
    return {
      period: periodOf(p),
      value: count > 0 ? weighted / count : null,
      previous: null,
      breakdown,
    };
  });
}

/** Workflow items: created per month, with completed and overdue as parts. */
export function adaptWorkflowItems(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.periods).map((p) => ({
    period: periodOf(p),
    value: numOr0(p.created_count),
    previous: null,
    breakdown: {
      created: numOr0(p.created_count),
      completed: numOr0(p.completed_count),
      overdue: numOr0(p.overdue_count),
    },
  }));
}

/** Staff absence rates (Part A): the sick rate per month and a year earlier. */
export function adaptAbsenceRates(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.periods).map((p) => {
    const previous = p.previous as Json | null | undefined;
    return {
      period: periodOf(p),
      value: num(p.sick_rate_pct),
      previous:
        previous && numOr0(previous.target_hours) > 0
          ? num(previous.sick_rate_pct)
          : null,
    };
  });
}

/**
 * The sickness rate of the whole period from the server summary (#1989 D10):
 * hours-weighted over the months and counted up to `as_of`, the number the
 * Absences screen shows. The previous year counts only when it had a target.
 */
export function adaptAbsenceRatesSummary(response: unknown): MetricSummaryTotals | null {
  const summary = (response as Json | undefined)?.summary as Json | undefined;
  if (!summary || typeof summary !== 'object') return null;
  const previous = summary.previous as Json | null | undefined;
  return {
    total: num(summary.sick_rate_pct),
    previous:
      previous && numOr0(previous.target_hours) > 0 ? num(previous.sick_rate_pct) : null,
  };
}

/**
 * Reconciliation monthly trend: what was outstanding at each month's end, parents
 * and offices as parts.
 */
export function adaptOutstandingTrend(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.points).map((p) => ({
    period: periodOf(p, 'month'),
    value: numOr0(p.outstandingCents),
    previous: null,
    breakdown: {
      parent: numOr0(p.outstandingParentCents),
      subsidy: numOr0(p.outstandingSubsidyCents),
    },
  }));
}

/** Reconciliation monthly trend: billed per month, parents and offices as parts. */
export function adaptBilledTrend(response: unknown): MetricPoint[] {
  return asArray((response as Json | undefined)?.points).map((p) => ({
    period: periodOf(p, 'month'),
    value: numOr0(p.billedCents),
    previous: null,
    breakdown: {
      parent: numOr0(p.billedParentCents),
      subsidy: numOr0(p.billedSubsidyCents),
    },
  }));
}

/**
 * Each month's previous value from the same month a year earlier in the same
 * series (#1989 D13), for an endpoint without its own comparison. A month whose
 * year-ago month is not in the series keeps no previous value.
 */
export function withYearAgo(points: MetricPoint[]): MetricPoint[] {
  const byMonth = new Map(points.map((p) => [p.period, p]));
  return points.map((p) => {
    const ago = byMonth.get(`${Number(p.period.slice(0, 4)) - 1}${p.period.slice(4)}`);
    return { ...p, previous: p.previous ?? ago?.value ?? null };
  });
}
